import React, { useState, useEffect } from "react";
import {
  HashRouter as Router,
  Routes,
  Route,
  Navigate,
} from "react-router-dom";
import {
  ThemeProvider,
  ScopedCssBaseline,
  useMediaQuery,
  Box,
  IconButton,
} from "@mui/material";
import { useResponsiveTheme } from "./theme";
import "./App.css";
import EventsList from "./components/EventsList";
import MarketsList from "./components/MarketsList";
import { BookmakersProvider } from "./contexts/BookmakersContext";
import { DarkModeContext } from "./contexts/DarkModeContext";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ValuePage from "./components/ValueBets/ValuePage";
import SGMPage from "./components/SGM/SGMPage";

// Pro-only route wrapper component
const ProRoute = ({ element }) => {
  const isPro = window.membershipLevel === 30;
  return isPro ? element : <Navigate to="/" replace />;
};

function App() {
  // TODO: Move theming logic to common
  function useThemeObserver(setMode) {
    useEffect(() => {
      const target = document.documentElement;

      const observer = new MutationObserver((mutations) => {
        for (const mutation of mutations) {
          if (mutation.attributeName === 'class') {
            const themeClass = Array.from(target.classList).find(cls =>
                cls.startsWith('theme-')
            );
            if (themeClass) {
              setMode(themeClass.replace('theme-', ''))
            }
          }
        }
      });

      observer.observe(target, { attributes: true });

      return () => observer.disconnect();
    }, [setMode]);
  }

  const [mode, setMode] = useState(() => {
    const documentClasses = document.documentElement.classList;
    if (documentClasses.contains('theme-dark') || documentClasses.contains('theme-light')) {
      return documentClasses.contains('theme-dark') ? 'dark' : 'light';
    }

    return window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light";
  });

  useThemeObserver(setMode);

  const darkMode = mode === 'dark'
  const [showBackToTop, setShowBackToTop] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      if (window.pageYOffset > 300) {
        setShowBackToTop(true);
      } else {
        setShowBackToTop(false);
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  };

  const theme = useResponsiveTheme(darkMode ? "dark" : "light");

  return (
    <DarkModeContext.Provider value={{ darkMode }}>
      <ScopedCssBaseline>
        <ThemeProvider theme={theme}>
          <BookmakersProvider>
            <Router>
              <Box
                sx={{
                  display: "flex",
                  position: "relative",
                  minHeight: "100vh",
                  backgroundColor: darkMode ? "#1E1E1E" : "inherit",
                  color: darkMode ? "text.primary" : "inherit",
                }}
              >
                <Box
                  sx={{
                    width: "100%",
                    mb: 4,
                    mt: 4,
                    backgroundColor: darkMode ? "#1E1E1E" : "inherit",
                    color: darkMode ? "text.primary" : "inherit",
                  }}
                >
                  <Routes>
                    <Route path="/" element={<EventsList />} />
                    <Route path="/event/:eventId" element={<MarketsList />} />
                    <Route path="/value" element={<ValuePage />} />
                    <Route
                      path="/sgm/:eventId"
                      element={<ProRoute element={<SGMPage />} />}
                    />
                  </Routes>
                </Box>
                {showBackToTop && (
                  <IconButton
                    onClick={scrollToTop}
                    sx={{
                      position: "fixed",
                      bottom: 32,
                      right: 128,
                      backgroundColor: "primary.main",
                      color: "white",
                      "&:hover": {
                        backgroundColor: "primary.dark",
                      },
                    }}
                  >
                    <ArrowUpwardIcon />
                  </IconButton>
                )}
              </Box>
            </Router>
          </BookmakersProvider>
        </ThemeProvider>
      </ScopedCssBaseline>
    </DarkModeContext.Provider>
  );
}

export default App;
